import { useEffect, useState } from "react";
import { Button, Row, Col, } from "rsuite";
import { useTypedDispatch, useTypedSelector } from "App/Store";
import { hideLoader, showLoader } from "redux/action/loaderAction";
import { useQuery } from "shared/hooks/useQuery";
import history from "App/History";
import Pagination from "components/Pagination";
import StoreCard from "components/StoreCard";
import { StoreType } from "core/models/Store";
import { getStoreList } from "redux/action/storeListAction";
import TopupIcon from "shared/Icons/SideBarIcons/Topup";
import GiftCardIcon from "shared/Icons/SideBarIcons/GiftCard";
import PromptModal from "shared/components/PromptModal";
import { deleteSeagmProduct } from "core/services/Store";
import { useNotification } from "shared/hooks/useNotification";
import { DEFAULT_API_ERROR } from "core/constants/Defaults";

const PAGE_SIZE = 6;

export default function StoreProducts() {
  const dispatch = useTypedDispatch();
  const { isLoading, stores, count, currentPage } = useTypedSelector(
    (state) => state.stores
  );
  const query = useQuery();
  const [search, setSearch] = useState("");
  const [storeType, setStoreType] = useState<"topup" | "giftcard">("topup");
  const [selectedStore, setSelectedStore] = useState<StoreType | null>(null);
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const showNotification = useNotification();

  useEffect(() => {
    const params: any = { page: 0, size: PAGE_SIZE };
    if (query.get("page") && !isNaN(Number(query.get("page")))) {
      params.page = Number(query.get("page"));
    }

    const searchText = query.get("search");
    if (searchText) {
      setSearch(searchText.trim());
      params.search = searchText.trim();
    }

    const type = query.get("type") as "topup" | "giftcard";
    if (type) {
      setStoreType(type);
      params.type = type;
    } else {
      params.type = storeType;
    }

    dispatch(getStoreList(params));
  }, [dispatch, query, storeType]);

  useEffect(() => {
    if (isLoading) {
      dispatch(showLoader());
    } else {
      dispatch(hideLoader());
    }
  }, [isLoading, dispatch]);

  function pushParams(newParams: Record<string, any>) {
    history.push({
      pathname: "/store",
      search: new URLSearchParams(newParams).toString(),
    });
  }

  function handlePageChange(pageNumber: number) {
    const params: any = { page: pageNumber - 1, type: storeType };
    const searchText = search.trim();
    if (searchText) params.search = searchText;
    pushParams(params);
  }

  function handleAddStore() {
    history.push("/store/add");
  }

  function handleStoreDetail(storeId: number) {
    history.push(`/store/edit/${storeId}`);
  }

  function handleOpenDeleteModal(store: StoreType) {
    setSelectedStore(store);
    setOpenDeleteModal(true);
  }

  function handleCloseDeleteModal() {
    setOpenDeleteModal(false);
    setTimeout(() => setSelectedStore(null), 300);
  }

  function buildListParams() {
    const params: any = { page: 0, size: PAGE_SIZE, type: storeType };
    const pageParam = query.get("page");
    if (pageParam && !isNaN(Number(pageParam))) {
      params.page = Number(pageParam);
    }
    const searchText = query.get("search");
    if (searchText) params.search = searchText.trim();
    return params;
  }

  async function handleDeleteStore() {
    if (!selectedStore) return;
    dispatch(showLoader());
    const deleteRes = await deleteSeagmProduct(selectedStore.id);
    if (deleteRes.status) {
      const successMessage =
        deleteRes.data?.message || deleteRes.message || "Store deleted successfully";
      showNotification({
        message: successMessage,
        title: "Success",
        type: "success",
      });
      dispatch(getStoreList(buildListParams()));
    } else {
      showNotification({
        message: deleteRes.message || DEFAULT_API_ERROR,
        title: "Error",
        type: "error",
      });
    }
    dispatch(hideLoader());
    handleCloseDeleteModal();
  }

  function handleTabChange(type: "topup" | "giftcard") {
    setStoreType(type);
    const params: any = { page: 0, type };
    const searchText = search.trim();
    if (searchText) params.search = searchText;
    pushParams(params);
  }

  return (
    <>
      <div className="store-section-bar">
        <div className="store-tabs">
          <div
            className={`tab-item ${storeType === "topup" ? "active" : ""}`}
            onClick={() => handleTabChange("topup")}
          >
            Top-Ups
          </div>
          <div
            className={`tab-item ${storeType === "giftcard" ? "active" : ""}`}
            onClick={() => handleTabChange("giftcard")}
          >
            Gift Cards
          </div>
        </div>
        <Button appearance="primary" onClick={handleAddStore} className="btn">
          Add Product
        </Button>
      </div>

      {!!stores.length ? (
        <>
          <Row gutter={20}>
            {stores.map((store: StoreType) => (
              <Col xs={24} sm={24} md={12} lg={8} key={store.id}>
                <StoreCard
                  openAddStore={handleStoreDetail}
                  onDeleteStore={handleOpenDeleteModal}
                  store={store}
                />
              </Col>
            ))}
          </Row>
          {count > PAGE_SIZE && (
            <div className="pagination">
              <Pagination
                pages={Math.ceil(count / PAGE_SIZE)}
                limit={10}
                activePage={currentPage + 1}
                maxButtons={6}
                onPageChange={handlePageChange}
              />
            </div>
          )}
        </>
      ) : (
        <div className="store-dummy-container">
          {storeType === "topup" ? (
            <TopupIcon fontSize={350} />
          ) : (
            <GiftCardIcon fontSize={350} />
          )}
          {storeType === "topup" ? (
            <>
              <span className="store-dummy-text">No top-ups added yet </span>
              <span className="store-dummy-content">
                Looks like you haven’t created any top-ups yet. Create one to
                get started.
              </span>
            </>
          ) : (
            <>
              <span className="store-dummy-text">No gift cards added yet </span>
              <span className="store-dummy-content">
                Looks like you haven’t created any gift cards yet. Create one to
                get started.
              </span>
            </>
          )}
        </div>
      )}
      <PromptModal
        open={openDeleteModal}
        onAccept={handleDeleteStore}
        onReject={handleCloseDeleteModal}
        acceptText="Delete"
        rejectText="Cancel"
        title="Delete SEAGM Product"
        message={`Are you sure you want to delete ${selectedStore?.name || "this product"}?`}
      />
    </>
  );
}
